/*
 * CONTACT FORM — the customer service form of the help page, for any theme.
 * The message goes to the shop's inbox / support ticket (POST /api/contact); the
 * topic (contact category, Prodyflow settings/customer-service) decides the
 * extra form elements and whether files can be attached.
 *
 * All logic is here (topics, extra fields, files, validation, prefill of a
 * logged in customer, sending); the theme gives the in-DOM Vue template:
 *   <div data-contact-app data-delimiters="[[,]]" data-texts="#json-script-id" data-type="1">
 *     <form @submit.prevent="send"> ... [[ ]] template using the data / methods below ... </form>
 *   </div>
 * Data for the markup: categories, category, cat, catFields, values {name,email,phone,subject,body},
 * fields, files, missing (keys of the empty required inputs), sending, done, error, loggedIn.
 * Methods: t(key, fallback), fieldLabel(f), optionLabel(o), pickFiles($event), send(), again().
 * The inputs' floating label: `filled(key)` for the class on the .form-group.
 *
 * The backend stores the message with the topic, the e-mail, the name, the phone,
 * the subject, the text and the answers.
 */
const ContactForm = (() => {
    let app = null;
    let root = null;

    const token = () => (window.Cookies && Cookies.getItem('login_token')) || '';
    const headers = () => (window.http && typeof http.headers === 'function') ? http.headers() : { Accept: 'application/json' };

    const component = (el) => {
        let texts = {};
        try {
            const src = el.dataset.texts ? document.querySelector(el.dataset.texts) : null;
            texts = src ? JSON.parse(src.textContent) : {};
        } catch (e) { texts = {}; }
        const lan = () => window.lan || document.documentElement.lang || 'en';
        return {
            data() {
                return {
                    texts,
                    categories: null, category: null, fields: {}, files: [],
                    values: { name: '', email: '', phone: '', subject: '', body: '' },
                    missing: [], sending: false, done: false, error: '', loggedIn: false,
                };
            },
            computed: {
                cat() { return (this.categories || []).find((c) => c.id === this.category) || null; },
                catFields() { return this.cat ? this.cat.fields : []; },
            },
            methods: {
                t(key, fallback) { return this.texts[key] ?? fallback ?? ''; },
                filled(key) { return String(this.values[key] ?? '').length > 0; },
                fieldLabel(f) { const l = f.labels || {}; return l[lan()] || l.en || Object.values(l)[0] || f.key; },
                optionLabel(o) { const l = o.labels || {}; return l[lan()] || l.en || Object.values(l)[0] || o.value; },
                pickFiles(e) { this.files = Array.from(e.target.files).slice(0, 3); },
                topicChanged() { this.fields = {}; this.files = []; },
                async loadCategories() {
                    try {
                        const r = await fetch('/api/contact-categories', { method: 'POST', headers: headers(), body: JSON.stringify({ type: Number(el.dataset.type) || 1 }) });
                        const d = r.ok ? await r.json() : {};
                        this.categories = (d.data || []).map((c) => {
                            const tr = c.contactCategoryTranslations || [];
                            return { id: c.id, name: ((tr.find((x) => x.language_code === lan()) || tr.find((x) => x.language_code === 'en') || tr[0] || {}).name) || '', fields: c.fields || [], allowFiles: !!c.allow_files };
                        });
                        if (this.categories.length && !this.category) this.category = this.categories[0].id;
                    } catch (e) { this.categories = []; this.error = this.t('e_load', 'Could not load the form.'); }
                },
                // a logged in customer: name, e-mail, phone from the profile (what the customer already typed stays)
                async prefill() {
                    const tk = token();
                    if (!tk || !window.ProfileSession) return;
                    this.loggedIn = true;
                    const info = await window.ProfileSession.userInfo(tk);
                    if (!info) return;
                    const contacts = info.crmItemContacts || (info.crmItemContact ? [].concat(info.crmItemContact) : []);
                    const contact = contacts.find((c) => c && (c.default === true || c.is_default === true)) || contacts[0] || {};
                    const set = (key, value) => { if (!this.values[key] && value) this.values[key] = value; };
                    set('name', [info.first_name, info.last_name].filter(Boolean).join(' '));
                    set('email', info.invoice_email || contact.email || info.email);
                    set('phone', contact.phone || info.phone);
                },
                validate() {
                    const empty = (v) => !String(v ?? '').trim();
                    const need = ['name', 'email', 'phone', 'body'].filter((k) => empty(this.values[k]));
                    if (!need.includes('email') && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(this.values.email.trim())) need.push('email');
                    this.catFields.filter((f) => f.required && empty(this.fields[f.key])).forEach((f) => need.push('f:' + f.key));
                    this.missing = need;
                    return need.length === 0;
                },
                async send() {
                    this.error = '';
                    if (!this.category) return;
                    if (!this.validate()) { this.error = this.t('e_required', 'Please fill in the required fields.'); return; }
                    this.sending = true;
                    const v = this.values;
                    const body = new FormData();
                    body.append('token', token());
                    body.append('contact_category_id', this.category);
                    body.append('email', v.email.trim());
                    body.append('text', v.body.trim());
                    body.append('name', v.name.trim());
                    body.append('phone', v.phone.trim());
                    if (v.subject.trim()) body.append('subject', v.subject.trim());
                    if (this.catFields.length) body.append('fields', JSON.stringify(this.fields));
                    if (this.cat && this.cat.allowFiles) this.files.forEach((f) => body.append('files[]', f));
                    const h = headers();
                    delete h['Content-Type'];
                    try {
                        const r = await fetch('/api/contact', { method: 'POST', headers: h, body });
                        const d = await r.json().catch(() => ({}));
                        if (!r.ok) {
                            const first = d && d.errors ? Object.values(d.errors).flat()[0] : '';
                            throw new Error(first || (d && d.message) || this.t('e_send', 'Sending failed. Please try again.'));
                        }
                        this.done = true;
                    } catch (e) {
                        this.error = (e && e.message) || this.t('e_send', 'Sending failed. Please try again.');
                    }
                    this.sending = false;
                },
                again() { this.done = false; this.values.body = ''; this.values.subject = ''; this.fields = {}; this.files = []; this.missing = []; },
            },
            mounted() { this.loadCategories(); this.prefill(); },
        };
    };

    const mount = () => {
        if (root) return root;
        const el = document.querySelector('[data-contact-app]');
        if (!el || typeof Vue === 'undefined') return null;
        const comp = component(el);
        if (el.dataset.delimiters) comp.delimiters = el.dataset.delimiters.split(',');
        app = Vue.createApp(comp);
        root = app.mount(el);
        return root;
    };

    const boot = () => setTimeout(mount, 0);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

    return { mount, get app() { return root; } };
})();

window.ContactForm = ContactForm;
